import { ApolloError } from '@apollo/client';
import { client } from '@client/graphql/client.ts';
import { useAuth } from '@client/module/auth/auth-context.ts';
import useCheckAdminRole from '@client/module/auth/check-role-hook';
import { formatGraphQlError, validationErrors } from '@client/module/error/error.ts';
import CompanySocialSetting from '@client/page/management/company-settings/company-social-setting.tsx';
import { useCompanyHubpageLink } from '@client/page/management/company-settings/logic/use-company-hubpage-link.ts';
import { useCompanySettings } from '@client/page/management/company-settings/logic/use-company-setting.ts';
import { useUpdateHubpageAndCompany } from '@client/page/management/company-settings/logic/use-update-hubpage.ts';
import { useCompanyService } from '@packages/core/company/company-context.ts';
import { useCompanyId } from '@packages/core/company/focused-company-context.ts';
import ImageInput from '@packages/core/uploading/image-input.tsx';
import PrimaryButton from '@packages/ui/button/primary-button.tsx';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import CardContent from '@packages/ui/card/card-content.tsx';
import CardHeader from '@packages/ui/card/card-header.tsx';
import Card from '@packages/ui/card/card.tsx';
import { ContentLayout, PrimaryContent } from '@packages/ui/content-layout.tsx';
import FormItem from '@packages/ui/form/form-item.tsx';
import FormLayout from '@packages/ui/form/form-layout.tsx';
import TextInput from '@packages/ui/form/input/text-input.tsx';
import ToggleInput from '@packages/ui/form/input/toggle-input.tsx';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import { captureException } from '@sentry/browser';
import { useTranslate } from '@tolgee/react';
import { ChangeEvent, useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useUpdateBranch } from '../branch/branch-edit/logic/use-update-branch';
import { useFocusedBranchContext } from '../table-group/table-group-list/component/branch-selection/focused-branch-context';
import CompanyImprintSetting from './company-imprint-setting';

const MAX_FILES = 9;
// Unit: mb
const MAX_FILE_SIZES = 15;

interface FormValues {
  description: string | null;
  subDomain: string;
  banner: string;
  images: string[];
  alternateUrl: string | null;
  alternateUrlEnable: boolean;
  alternateBookingUrl: string | null;
  alternateOrderUrl: string | null;

  // Imprint
  imprintOwner?: string | null;
  imprintOwnerTaxId?: string | null;
  imprintCompanyName?: string | null;
  imprintCompanyTaxId?: string | null;
  imprintPhone?: string | null;
  imprintEmail?: string | null;

  // Basic
  logo: string | null;

  // social
  instagramLink: string | null;
  facebookLink: string | null;
  googleLink: string | null;
  tiktokLink: string | null;

  // branch
  priceListLink: string | null;
}

export default function HubPageSetting() {
  const { showAlert } = useNotificationCenter();
  const { t } = useTranslate();
  const { isAdmin } = useCheckAdminRole();

  const companyService = useCompanyService();
  const branch = useFocusedBranchContext().branch;

  const companyID = useCompanyId();
  const { data, refetch } = useCompanySettings(companyID);

  const { auth } = useAuth();
  const form = useForm<FormValues>({
    defaultValues: {
      logo: '',
      instagramLink: '',
      facebookLink: '',
      googleLink: '',
      tiktokLink: '',

      imprintOwner: '',
      imprintOwnerTaxId: '',
      imprintCompanyName: '',
      imprintCompanyTaxId: '',
      imprintPhone: '',
      imprintEmail: '',

      description: '',
      subDomain: '',
      banner: '',
      images: [],
      alternateUrl: '',
      alternateUrlEnable: false,
      alternateBookingUrl: '',
      alternateOrderUrl: '',

      priceListLink: '',
    },
    mode: 'onChange',
    criteriaMode: 'all',
    reValidateMode: 'onBlur',
  });
  const { register, handleSubmit, control, setError, reset, watch } = form;

  useEffect(() => {
    if (data?.company) {
      reset({
        logo: companyService.company?.logo,
        instagramLink: companyService.company?.instagramLink ?? '',
        facebookLink: companyService.company?.facebookLink ?? '',
        googleLink: companyService.company?.googleLink ?? '',
        tiktokLink: companyService.company?.tiktokLink ?? '',

        description: data.company.hubPage?.description,
        subDomain: data.company.hubPage?.subDomain,
        banner: data.company.hubPage?.banner ?? '',
        images: data.company.hubPage?.images ?? [],
        alternateUrl: data.company.hubPage?.alternateUrl,
        alternateUrlEnable: data.company.hubPage?.alternateUrlEnable,
        alternateBookingUrl: data.company.hubPage?.alternateBookingUrl,
        alternateOrderUrl: data.company.hubPage?.alternateOrderUrl,

        imprintOwner: companyService.company?.imprintOwner ?? '',
        imprintOwnerTaxId: companyService.company?.imprintOwnerTaxId ?? '',
        imprintCompanyName: companyService.company?.imprintCompanyName ?? '',
        imprintCompanyTaxId: companyService.company?.imprintCompanyTaxId ?? '',
        imprintPhone: companyService.company?.imprintPhone ?? '',
        imprintEmail: companyService.company?.imprintEmail ?? '',
        priceListLink: branch?.priceListLink ?? '',
      });
    }
  }, [
    branch?.priceListLink,
    companyService.company?.facebookLink,
    companyService.company?.googleLink,
    companyService.company?.imprintCompanyName,
    companyService.company?.imprintCompanyTaxId,
    companyService.company?.imprintEmail,
    companyService.company?.imprintOwner,
    companyService.company?.imprintOwnerTaxId,
    companyService.company?.imprintPhone,
    companyService.company?.instagramLink,
    companyService.company?.logo,
    companyService.company?.tiktokLink,
    data,
    reset,
  ]);

  const [update] = useUpdateHubpageAndCompany();
  const [updateBranch] = useUpdateBranch();

  const onSubmit = async (updateData: FormValues) => {
    try {
      // Thực hiện cả 2 mutation song song nhau bằng Promise.all
      const [hubpageResult, branchResult] = await Promise.all([
        // Update hubpage và company
        update({
          variables: {
            companyID: companyID,
            input: {
              description: updateData.description?.trim(),
              subDomain: updateData.subDomain.trim(),
              banner: updateData.banner,
              images: updateData.images,
              alternateUrl: updateData.alternateUrlEnable
                ? updateData.alternateUrl
                  ? updateData.alternateUrl.trim()
                  : null
                : null,
              alternateUrlEnable: updateData.alternateUrlEnable,
              alternateBookingUrl: updateData.alternateBookingUrl
                ? updateData.alternateBookingUrl.trim()
                : null,
              alternateOrderUrl: updateData.alternateOrderUrl ? updateData.alternateOrderUrl.trim() : null,
            },
            companyInput: {
              name: companyService.company?.name ?? '',
              logo: updateData.logo,
              instagramLink: updateData.instagramLink,
              facebookLink: updateData.facebookLink,
              googleLink: updateData.googleLink,
              tiktokLink: updateData.tiktokLink,
              imprintOwner: updateData.imprintOwner,
              imprintOwnerTaxId: updateData.imprintOwnerTaxId,
              imprintCompanyName: updateData.imprintCompanyName,
              imprintCompanyTaxId: updateData.imprintCompanyTaxId,
              imprintPhone: updateData.imprintPhone,
              imprintEmail: updateData.imprintEmail,
            },
          },
        }),

        // Update branch nếu có branch.branch?.id
        branch?.id
          ? updateBranch({
              variables: {
                id: branch.id,
                input: {
                  priceListLink: updateData.priceListLink,
                  name: branch.name,
                  streetAddress: branch.streetAddress,
                  addressLocality: branch.addressLocality,
                  addressRegion: branch.addressRegion,
                  postalCode: branch.postalCode,
                  addressCountry: branch.addressCountry,
                  contactPhone: branch.contactPhone,
                },
              },
            })
          : Promise.resolve(null), // Trả về null nếu không có branch ID
      ]);

      // Kiểm tra kết quả từ cả 2 mutation
      if (hubpageResult.data && branchResult?.data) {
        await refetch();
        showAlert({
          status: 'success',
          title: t('company.setting.hubpage.update.alert.title', 'Update Success'),
          message: t('company.setting.hubpage.update.alert.message', 'Update hub page successfully'),
        });
      }
    } catch (err) {
      if (err instanceof ApolloError) {
        const applicationErrors = formatGraphQlError(err.graphQLErrors);
        const validationError = validationErrors(applicationErrors);

        for (const field in validationError) {
          setError(
            field as keyof FormValues,
            {
              type: 'server',
              message: t(validationError[field] ?? 'validation-error.common.alert'),
            },
            { shouldFocus: true }
          );
        }

        showAlert({
          title: t('common.error.update.title', 'Update failed'),
          message: err.message,
          status: 'error',
        });
      }
      captureException(err);
    }
  };

  return (
    <ContentLayout>
      <PrimaryContent>
        <Card className="">
          <CardHeader title={'Hub Page Setting'}>
            <PrimaryButton
              id="create-button"
              data-testid="create-button"
              className="w-32"
              disabled={!form.formState.isDirty}
              onClick={() => {
                handleSubmit(onSubmit)().catch(captureException);
              }}
            >
              {t('common.button.update')}
            </PrimaryButton>
          </CardHeader>
        </Card>

        <Card>
          <CardContent>
            <form
              className={'space-y-6'}
              onSubmit={() => {
                handleSubmit(onSubmit);
              }}
            >
              {['ADMIN', 'SUPER_ADMIN'].some((role) => auth?.role.includes(role)) && (
                <FormItem title={t('company-setting.hubpage.subdomain.title', 'Sub Domain')} className="max-w-md">
                  <input
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset
                 ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 "
                    {...register('subDomain', {
                      required: t('company-setting.hubpage.subdomain.validate.required', 'Subdomain is required'),
                      minLength: {
                        value: 2,
                        message: t(
                          'company-setting.hubpage.subdomain.validate.min-length',
                          'Subdomain must be at least {amount} characters long',
                          {
                            amount: 2,
                          }
                        ),
                      },
                      maxLength: {
                        value: 100,
                        message: t(
                          'company-setting.hubpage.subdomain.validate.max-length',
                          'Subdomain must be at most {amount} characters long',
                          {
                            amount: 100,
                          }
                        ),
                      },
                      pattern: {
                        value: /^[a-z0-9-]+$/,
                        message: t(
                          'company-setting.hubpage.subdomain.validate.lowercase',
                          'Subdomain must be lowercase and no space'
                        ),
                      },
                      validate: (value) => {
                        if (value === 'inhouse') {
                          return t(
                            'company-setting.hubpage.subdomain.validate.special.inhouse',
                            'The subdomain you entered is a special case, please try again.'
                          );
                        }
                      },
                    })}
                    placeholder={'[placeholder].96digital.de'}
                  ></input>
                  {form.formState.errors.subDomain && (
                    <div className="text-red-500 text-sm">{form.formState.errors.subDomain.message}</div>
                  )}
                </FormItem>
              )}

              <FormItem title={t('company-setting.hubpage.logo.title', 'Logo')} className="max-w-md">
                <Controller
                  control={control}
                  name="logo"
                  render={({ field: { onChange, value } }) => (
                    <ImageInput
                      client={client}
                      images={value ? [value] : []}
                      onChange={(newValue) => {
                        if (newValue.length > 0) {
                          onChange(newValue[0]);
                        } else {
                          onChange(null);
                        }
                      }}
                    />
                  )}
                />
              </FormItem>

              <FormItem title={t('company-setting.hubpage.banner.title', 'Top banner')}>
                <div>
                  <Controller
                    name="banner"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <ImageInput
                          client={client}
                          images={field.value ? [field.value] : []}
                          onChange={(newValue) => {
                            if (newValue.length === 0) {
                              field.onChange(''); // Use field.onChange for proper field registration
                            } else {
                              field.onChange(newValue[0]);
                            }
                          }}
                        />
                        {error && <div className="text-red-500 text-sm">{error.message}</div>}
                      </>
                    )}
                  />
                </div>
              </FormItem>

              <FormItem title={t('company-setting.hubpage.description.title', 'Description')} className="max-w-md">
                <textarea
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset
                 ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  {...register('description')}
                />
                {form.formState.errors.description && (
                  <div className="text-red-500 text-sm">{form.formState.errors.description.message}</div>
                )}
              </FormItem>
              <CopyLink />
            </form>
          </CardContent>
        </Card>

        {isAdmin && (
          <Card>
            <CardHeader title={t('company-setting.hubpage.alternateEnable.title', 'Alternate url enable')} />

            <CardContent>
              <FormLayout>
                <FormItem title={t('company-setting.hubpage.alternateEnable.title', 'Alternate url enable')}>
                  <div>
                    <Controller
                      name="alternateUrlEnable"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <div className="max-w-xl text-gray-500 italic mb-4 text-sm">
                            {t(
                              'hubpage.alternateUrl.description',
                              'When you enable this option, customers will be' +
                                ' redirected to the link you provide below when they attempt to access the systems hubpage.'
                            )}
                          </div>
                          <ToggleInput
                            value={field.value}
                            onChange={(newVal) => {
                              field.onChange(newVal);
                            }}
                          />
                          {error && <div className="text-red-500 text-sm">{error.message}</div>}
                        </>
                      )}
                    />
                  </div>
                </FormItem>

                {watch('alternateUrlEnable') && (
                  <FormItem
                    title={t('company-setting.hubpage.alternate-url.title', 'Alternate url')}
                    className={'max-w-xl'}
                  >
                    <div>
                      <Controller
                        name="alternateUrl"
                        control={control}
                        render={({ field }) => {
                          return (
                            <>
                              <TextInput
                                type="text"
                                label="alternateUrl"
                                value={field.value ?? ''}
                                name="alternateUrl"
                                placeholder={t('company-setting.hubpage.alternate-url.placeholder', 'https://')}
                                onChange={field.onChange}
                              />
                              {form.formState.errors.alternateUrl && (
                                <div className="text-red-500 text-sm">{form.formState.errors.alternateUrl.message}</div>
                              )}
                            </>
                          );
                        }}
                      />
                    </div>
                  </FormItem>
                )}
              </FormLayout>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader title={t('company-setting.hubpage.price-list-link.title', 'Price list link')} />

          <CardContent>
            <FormLayout>
              <FormItem title={t('company-setting.hubpage.price-list-link.title', 'Price list link')}>
                <div>
                  <Controller
                    name="priceListLink"
                    control={control}
                    rules={{
                      pattern: {
                        value: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/,
                        message: t(
                          'company-setting.hubpage.price-list-link.error',
                          'Please enter a valid website link'
                        ),
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <div className="max-w-xl text-gray-500 italic mb-4 text-sm">
                          {t(
                            'company-setting.hubpage.price-list-link.description',
                            'Add a link to your price list that will be displayed on your hub page'
                          )}
                        </div>
                        <TextInput
                          type="text"
                          label="priceListLink"
                          value={field.value ?? ''}
                          name={field.name}
                          placeholder={t('company-setting.hubpage.price-list-link.placeholder', 'https://')}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            field.onChange(e.target.value);
                          }}
                        />
                        {error && <div className="text-red-500 text-sm">{error.message}</div>}
                      </>
                    )}
                  />
                </div>
              </FormItem>
            </FormLayout>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title={t('company-setting.hubpage.alternate-booking-url.title', 'Alternative Booking URL')} />

          <CardContent>
            <FormLayout>
              <FormItem title={t('company-setting.hubpage.alternate-booking-url.title', 'Alternative Booking URL')}>
                <div>
                  <Controller
                    name="alternateBookingUrl"
                    control={control}
                    rules={{
                      pattern: {
                        value: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/,
                        message: t(
                          'company-setting.hubpage.alternate-booking-url.error',
                          'Please enter a valid website link'
                        ),
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <div className="max-w-xl text-gray-500 italic mb-4 text-sm">
                          {t(
                            'company-setting.hubpage.alternate-booking-url.description',
                            'Add an alternative booking URL that will be used for booking redirects'
                          )}
                        </div>
                        <TextInput
                          type="text"
                          label="alternateBookingUrl"
                          value={field.value ?? ''}
                          name={field.name}
                          placeholder={t('company-setting.hubpage.alternate-booking-url.placeholder', 'https://')}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            field.onChange(e.target.value);
                          }}
                        />
                        {error && <div className="text-red-500 text-sm">{error.message}</div>}
                      </>
                    )}
                  />
                </div>
              </FormItem>
            </FormLayout>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title={t('company-setting.hubpage.alternate-order-url.title', 'Alternative Order URL')} />

          <CardContent>
            <FormLayout>
              <FormItem title={t('company-setting.hubpage.alternate-order-url.title', 'Alternative Order URL')}>
                <div>
                  <Controller
                    name="alternateOrderUrl"
                    control={control}
                    rules={{
                      pattern: {
                        value: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/,
                        message: t(
                          'company-setting.hubpage.alternate-order-url.error',
                          'Please enter a valid website link'
                        ),
                      },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <div className="max-w-xl text-gray-500 italic mb-4 text-sm">
                          {t(
                            'company-setting.hubpage.alternate-order-url.description',
                            'Add an alternative online order URL that replaces the pickup and delivery buttons'
                          )}
                        </div>
                        <TextInput
                          type="text"
                          label="alternateOrderUrl"
                          value={field.value ?? ''}
                          name={field.name}
                          placeholder={t('company-setting.hubpage.alternate-order-url.placeholder', 'https://')}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            field.onChange(e.target.value);
                          }}
                        />
                        {error && <div className="text-red-500 text-sm">{error.message}</div>}
                      </>
                    )}
                  />
                </div>
              </FormItem>
            </FormLayout>
          </CardContent>
        </Card>

        <FormProvider {...form}>
          <CompanySocialSetting />
        </FormProvider>

        <FormProvider {...form}>
          <CompanyImprintSetting />
        </FormProvider>

        {/*eslint-disable @typescript-eslint/prefer-nullish-coalescing*/}
        {(auth?.role.includes('SUPER_ADMIN') ||
          auth?.role.includes('ADMIN') ||
          auth?.userCompanyRole
            .find((item) => item.companyId == companyID)
            ?.permission.includes('MANAGE_NAIL_APPOINTMENT') ||
          auth?.userCompanyRole
            .find((item) => item.companyId == companyID)
            ?.permission.includes('MANAGE_ONLINE_ORDER') ||
          auth?.userCompanyRole
            .find((item) => item.companyId == companyID)
            ?.permission.includes('MANAGE_TABLE_RESERVATION')) && (
          <Card>
            <CardContent>
              <FormItem title={t('company-setting.hubpage.image.title', 'Images')}>
                <p className="text-xs text-gray-500 mb-4">
                  {t(
                    'hubpage.image.description',
                    'Upload up to {count} photos, each photo file no larger than {size} MB, best photo ratio is 1:1',
                    {
                      size: MAX_FILE_SIZES,
                      count: MAX_FILES,
                    }
                  )}
                </p>
                <Controller
                  control={control}
                  name="images"
                  render={({ field: { onChange, value } }) => (
                    <ImageInput
                      client={client}
                      images={value}
                      onChange={onChange}
                      multiple={true}
                      maxFiles={MAX_FILES}
                      maxSizes={MAX_FILE_SIZES}
                    />
                  )}
                />
              </FormItem>
            </CardContent>
          </Card>
        )}
      </PrimaryContent>
    </ContentLayout>
  );
}

const CopyLink = () => {
  const { showAlert } = useNotificationCenter();
  const { t } = useTranslate();

  const link = useCompanyHubpageLink();

  const copyLinkHandle = () => {
    void navigator.clipboard
      .writeText(link)
      .then(() => {
        showAlert({
          status: 'success',
          title: t('alert.title.success'),
          message: t('company-setting.hubpage.hubpage-link.alert.message.success', 'Link copied to clipboard'),
        });
      })
      .catch(() => {
        showAlert({
          status: 'error',
          title: t('alert.title.error'),
          message: t('company-setting.hubpage.hubpage-link.alert.message.error', 'Failed to copy link to clipboard'),
        });
      });
  };

  return (
    <FormItem title={t('Link')}>
      <SecondaryButton
        className="w-20"
        onClick={() => {
          copyLinkHandle();
        }}
      >
        {t('common.button.copy')}
      </SecondaryButton>
    </FormItem>
  );
};
